import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Modal,
  notification,
  theme,
  message,
  Breadcrumb,
} from "antd";
import {
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SIZES } from "../../theme/constant";
import { useSelector } from "react-redux";
import MasterDataAPI from "../../restAPI/MasterDataAPI";
import MeetingAPI from "../../restAPI/MeetingAPI";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import OctoplexLogo from "../../assets/image/logo/Octaplex Logo.png";

const loadImageAsDataUrl = (src, maxHeight = 80) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, maxHeight / img.naturalHeight);
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
    };
    img.onerror = reject;
    img.src = src;
  });

const { Title, Text } = Typography;

const reportTypeOptions = [
  { label: "All", value: "all" },
  { label: "Activity Log Report", value: "activity" },
  { label: "Annotation Summary Report", value: "annotation" },
];

const activityTagColor = (type) => {
  switch (type) {
    case "VIEWED":
      return "blue";
    case "ACKNOWLEDGED":
      return "green";
    case "DOWNLOADED":
    case "DOWNLOADED_ANNOTATED":
      return "cyan";
    case "BOOKMARKED":
      return "gold";
    case "TEXT_COMMENTED":
      return "purple";
    case "VOICE_COMMENTED":
      return "magenta";
    case "REPLIED":
      return "geekblue";
    case "HIGHLIGHTED":
      return "orange";
    case "DRAWN":
      return "volcano";
    case "COMMENT_UPDATED":
      return "lime";
    case "COMMENT_DELETED":
    case "VOICE_DELETED":
    case "DRAW_DELETED":
    case "HIGHLIGHT_DELETED":
    case "PAPER_DELETED":
    case "DOCUMENT_DELETED":
      return "red";
    case "MEETING_STATUS_CHANGED":
    case "PAPER_UNPUBLISHED":
      return "purple";
    case "DOCUMENT_UPLOADED":
    case "PAPER_UPLOADED":
      return "geekblue";
    case "MEETING_UPDATED":
    case "MINUTE_UPDATED":
    case "PAPER_UPDATED":
    case "DOCUMENT_UPDATED":
      return "processing";
    case "MEETING_CREATED":
    case "PAPER_PUBLISHED":
      return "green";
    case "MINUTE_DOWNLOADED":
      return "cyan";
    default:
      return "default";
  }
};

const activityTypeLabel = (type) => {
  switch (type) {
    case "VIEWED": return "Viewed";
    case "ACKNOWLEDGED": return "Acknowledged";
    case "DOWNLOADED": return "Downloaded";
    case "DOWNLOADED_ANNOTATED": return "Downloaded (Annotated)";
    case "BOOKMARKED": return "Bookmarked";
    case "TEXT_COMMENTED": return "Commented";
    case "VOICE_COMMENTED": return "Voice Comment";
    case "REPLIED": return "Replied";
    case "HIGHLIGHTED": return "Highlighted";
    case "DRAWN": return "Drawn";
    case "COMMENT_UPDATED": return "Comment Updated";
    case "COMMENT_DELETED": return "Comment Deleted";
    case "VOICE_DELETED": return "Voice Deleted";
    case "DRAW_DELETED": return "Drawing Deleted";
    case "HIGHLIGHT_DELETED": return "Highlight Deleted";
    case "MEETING_STATUS_CHANGED": return "Status Changed";
    case "MEETING_UPDATED": return "Meeting Updated";
    case "DOCUMENT_UPLOADED": return "Document Uploaded";
    case "PAPER_UPLOADED": return "Paper Uploaded";
    case "PAPER_UNPUBLISHED": return "Paper Unpublished";
    case "PAPER_DELETED": return "Paper Deleted";
    case "DOCUMENT_DELETED": return "Document Deleted";
    case "MINUTE_DOWNLOADED": return "Minute Downloaded";
    case "PAPER_UPDATED": return "Paper Updated";
    case "PAPER_PUBLISHED": return "Paper Published";    case "DOCUMENT_UPDATED": return "Document Updated";
    case "MINUTE_UPDATED": return "Minute Updated";    case "MEETING_CREATED": return "Meeting Created";    default: return type || "—";
  }
};

const standardPagination = (total) => ({
  defaultPageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ["10", "20", "50", "100"],
  showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
  total,
});

export default function ActivityLogReport() {
  const {
    token: { colorBgContainer, borderRadiusLG, colorBorderSecondary },
  } = theme.useToken();

  const [form] = Form.useForm();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [committees, setCommittees] = useState([]);
  const [reportData, setReportData] = useState({ total: 0 });
  const [secretaryData, setSecretaryData] = useState([]);
  const [memberData, setMemberData] = useState([]);
  const [annotationData, setAnnotationData] = useState([]);
  const [annotationViewer, setAnnotationViewer] = useState({ open: false, content: '', record: null });
  const { companyId, token, userCompany } = useSelector((state) => state.auth);

  const selectedType = Form.useWatch('reportType', form) || 'all';

  
  useEffect(() => {
    (async () => {
      // clears form data upon company change
      handleClear();
      
      // fetch committees
      try {
        const companyInfo = userCompany.companyData.find(company => company.companyId == companyId);
        const committees = await MasterDataAPI.getCommitteeListDetailsByCompanyId(companyId, token);
        const mappedCommittees = (committees.data || [])
          .filter((c) =>
            c.status &&
            (c.secretaryId === companyInfo?.userDetailsId || c.assistantSecretaryId === companyInfo?.userDetailsId)
          )
          .map((c) => ({ label: c.name, value: c.id }));
        setCommittees([{ label: 'All', value: 'all' }, ...mappedCommittees]);
      } catch (err) {
        console.error('Failed to load committees:', err);
      }
    })();
  }, [companyId]);

  // Reset filters and clear results when company changes
  useEffect(() => {
    form.resetFields();
    setHasSearched(false);
    setReportData({ total: 0 });
    setSecretaryData([]);
    setMemberData([]);
    setAnnotationData([]);
  }, [companyId, form]);

  const handleSearch = useCallback(async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const params = {
        type: values.reportType || 'all',
        dateFrom: values.dateFrom.format('YYYY-MM-DD'),
        dateTo: values.dateTo.format('YYYY-MM-DD'),
        committeeId: values.committee && values.committee !== 'all' ? values.committee : undefined,
        meetingName: values.meeting || undefined,
        paperName: values.paperName || undefined,
        documentName: values.documentName || undefined,
        author: values.performedBy || undefined,
      };

      const res = await MeetingAPI.getActivityLogReport(params, companyId, token);
      const data = res?.data?.data || {};

      setSecretaryData((data.secretaryActivities || []).map((r, i) => ({
        key: `SEC-${r.id || i}`,
        activityId: `ACT-${String(r.id || i).padStart(5, '0')}`,
        datetime: r.datetime ? dayjs(r.datetime).format('YYYY-MM-DD HH:mm:ss') : '—',
        meeting: r.meetingName || '—',
        committee: r.committeeName || '—',
        paper: r.paperName || '—',
        document: r.documentName || '—',
        type: r.activityType,
        performedBy: r.performedBy || '—',
        changedField: r.changedField || '—',
        previousValue: r.previousValue || '—',
        newValue: r.newValue || '—',
      })));

      setMemberData((data.memberActivities || []).map((r, i) => ({
        key: `MEM-${r.id || i}`,
        activityId: `ACT-${String(r.id || i).padStart(5, '0')}`,
        datetime: r.datetime ? dayjs(r.datetime).format('YYYY-MM-DD HH:mm:ss') : '—',
        meeting: r.meetingName || '—',
        committee: r.committeeName || '—',
        paper: r.paperName || '—',
        document: r.documentName || '—',
        page: r.pageNumber || '—',
        type: r.activityType,
        performedBy: r.performedBy || '—',
        comment: r.newValue || '—',
      })));

      setAnnotationData((data.annotationSummary || []).map((r, i) => ({
        key: `ANN-${r.id || i}`,
        annotationId: `ANN-${String(r.id || i).padStart(5, '0')}`,
        datetime: r.datetime ? dayjs(r.datetime).format('YYYY-MM-DD HH:mm:ss') : '—',
        meeting: r.meetingName || '—',
        committee: r.committeeName || '—',
        paper: r.paperName || '—',
        document: r.documentName || '—',
        page: r.pageNumber || '—',
        annotationType: r.activityType,
        action: 'Created',
        content: r.newValue || '—',        
        annotatedBy: r.performedBy || '—',
      })));

      const secCount = (data.secretaryActivities || []).length;
      const memCount = (data.memberActivities || []).length;
      const annCount = (data.annotationSummary || []).length;

      setHasSearched(true);
      setReportData({ total: data.total || 0 });

      if (secCount + memCount + annCount === 0) {
        notificationApi.warning({
          message: 'No records found',
          description: 'No records found for the selected criteria. Please adjust your filters and try again.',
          placement: 'bottomLeft',
        });
      }
    } catch (err) {
      if (err?.errorFields) return; // Ant Design form validation error — inline messages shown
      console.error('Failed to fetch activity log report:', err);
      notificationApi.error({
        message: 'Report generation failed',
        description: 'Unable to generate report. Please try again later.',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, token, form]);

  const handleClear = () => {
    form.resetFields();
    setHasSearched(false);
    setReportData({ total: 0 });
    setSecretaryData([]);
    setMemberData([]);
    setAnnotationData([]);
  };

  const getDownloadParams = () => {
    const values = form.getFieldsValue();
    return {
      type: values.reportType || 'all',
      dateFrom: values.dateFrom ? values.dateFrom.format('YYYY-MM-DD') : undefined,
      dateTo: values.dateTo ? values.dateTo.format('YYYY-MM-DD') : undefined,
      committeeId: values.committee && values.committee !== 'all' ? values.committee : undefined,
      meetingName: values.meeting || undefined,
      paperName: values.paperName || undefined,
      documentName: values.documentName || undefined,
      author: values.performedBy || undefined,
    };
  };

  const triggerDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = async () => {
    try {
      setLoadingExcel(true);
      const params = getDownloadParams();
      const res = await MeetingAPI.downloadActivityLogReportExcel(params, companyId, token);
      triggerDownload(new Blob([res.data]), `Activity_Log_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch (err) {
      console.error('Excel download failed:', err);
      notificationApi.error({
        message: 'Excel export failed',
        description: 'Unable to download the Excel report. Please try again later.',
        placement: 'bottomLeft',
      });
    } finally {
      setLoadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setLoadingPdf(true);

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 32;
      const exportedAt = dayjs().format("YYYY-MM-DD HH:mm:ss");

      // --- Resolve filter values for PDF metadata ---
      const values = form.getFieldsValue();
      const companyInfo = userCompany?.companyData?.find(c => c.companyId == companyId);
      const userName = companyInfo?.name || '—';
      const userEmail = userCompany?.email || '';
      const companyNameStr = companyInfo?.companyName || 'Octoplex';
      const reportTypeLbl = reportTypeOptions.find(o => o.value === (values.reportType || 'all'))?.label || 'All';
      const dateFromStr = values.dateFrom ? values.dateFrom.format('DD MMM YYYY') : '—';
      const dateToStr = values.dateTo ? values.dateTo.format('DD MMM YYYY') : '—';
      const committeeLabel = values.committee === 'all' || !values.committee
        ? 'All committees'
        : (committees.find(c => c.value === values.committee)?.label || 'All committees');
      const currentType = values.reportType || 'all';

      // --- Header: logo + company info (left) and report title (right) ---
      const headerTop = 36;
      let logoBottom = headerTop;
      try {
        const { dataUrl, width: lw, height: lh } = await loadImageAsDataUrl(OctoplexLogo);
        const logoHeight = 28;
        const logoWidth = (lw / lh) * logoHeight;
        doc.addImage(dataUrl, "PNG", marginX, headerTop, logoWidth, logoHeight);
        logoBottom = headerTop + logoHeight;
      } catch (e) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(40, 60, 140);
        doc.text("OCTOPLEX", marginX, headerTop + 18);
        doc.setTextColor(0);
        logoBottom = headerTop + 22;
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(110);
      doc.text(`${companyNameStr}`, marginX, logoBottom + 12);
      doc.setTextColor(0);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Document Activity and Acknowledgement Status Report", pageWidth - marginX, headerTop + 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(80);
      const subtitleMap = { all: 'Activity Log Report  +  Annotation Summary Report', activity: 'Activity Log Report', annotation: 'Annotation Summary Report' };
      doc.text(subtitleMap[currentType] || subtitleMap.all, pageWidth - marginX, headerTop + 30, { align: "right" });
      doc.setTextColor(0);

      // Confidential pill
      const pillText = "Confidential — internal use only";
      doc.setFontSize(8);
      const pillTextWidth = doc.getTextWidth(pillText);
      const pillPaddingX = 6;
      const pillH = 14;
      const pillW = pillTextWidth + pillPaddingX * 2;
      const pillX = pageWidth - marginX - pillW;
      const pillY = headerTop + 38;
      doc.setFillColor(253, 232, 232);
      doc.roundedRect(pillX, pillY, pillW, pillH, 6, 6, "F");
      doc.setTextColor(170, 50, 50);
      doc.text(pillText, pillX + pillPaddingX, pillY + 10);
      doc.setTextColor(0);

      // Divider
      const dividerY = Math.max(logoBottom + 24, pillY + pillH + 8);
      doc.setDrawColor(200);
      doc.setLineWidth(0.5);
      doc.line(marginX, dividerY, pageWidth - marginX, dividerY);

      // --- Metadata band (lavender background) ---
      const metaTop = dividerY + 10;
      const metaPad = 14;
      const metaRowH = 30;
      const metaH = metaPad * 2 + metaRowH * 2 + 18;
      doc.setFillColor(238, 238, 248);
      doc.rect(marginX, metaTop, pageWidth - marginX * 2, metaH, "F");

      const metaColCount = 3;
      const metaColWidth = (pageWidth - marginX * 2 - metaPad * 2) / metaColCount;
      const metaStartX = marginX + metaPad;
      const metaLabelColor = 120;
      const metaValueColor = 30;

      const drawMetaCell = (label, value, col, row) => {
        const x = metaStartX + col * metaColWidth;
        const y = metaTop + metaPad + row * metaRowH;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(metaLabelColor);
        doc.text(label, x, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(metaValueColor);
        doc.text(value, x, y + 12);
      };

      drawMetaCell("GENERATED ON", exportedAt, 0, 0);
      drawMetaCell("EXPORTED BY", `${userName}${userEmail ? '  (' + userEmail + ')' : ''}`, 1, 0);
      drawMetaCell("REPORT TYPE", reportTypeLbl, 2, 0);
      drawMetaCell("DATE RANGE", `${dateFromStr} — ${dateToStr}`, 0, 1);
      drawMetaCell("COMMITTEE", committeeLabel, 1, 1);
      drawMetaCell("TOTAL RECORDS", `${totalEvents} records`, 2, 1);

      // Active filters row spanning full width
      const filtersY = metaTop + metaPad + metaRowH * 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(metaLabelColor);
      doc.text("ACTIVE FILTERS", metaStartX, filtersY);
      doc.setFontSize(9);
      doc.setTextColor(metaValueColor);
      const filterParts = [];
      if (values.meeting) filterParts.push(`Meeting: ${values.meeting}`);
      if (values.paperName) filterParts.push(`Paper: ${values.paperName}`);
      if (values.documentName) filterParts.push(`Document: ${values.documentName}`);
      if (values.performedBy) filterParts.push(`Performed by: ${values.performedBy}`);
      doc.text(
        filterParts.length > 0 ? filterParts.join('  ·  ') : 'No additional filters applied',
        metaStartX,
        filtersY + 12
      );

      // --- Summary stats ---
      const summaryTitleY = metaTop + metaH + 28;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text("Summary", marginX, summaryTitleY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const summaryY = summaryTitleY + 18;
      const summaryItems = [];
      summaryItems.push({ value: String(totalEvents), label: "Total events" });
      if (currentType === 'all' || currentType === 'activity') {
        summaryItems.push({ value: String(secretaryData.length), label: "Secretary activities" });
        summaryItems.push({ value: String(memberData.length), label: "Member activities" });
      }
      if (currentType === 'all' || currentType === 'annotation') {
        summaryItems.push({ value: String(annotationData.length), label: "Annotations" });
      }
      const summaryColWidth = (pageWidth - marginX * 2) / summaryItems.length;
      summaryItems.forEach((item, idx) => {
        const x = marginX + idx * summaryColWidth;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(item.value, x, summaryY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(110);
        doc.text(item.label, x, summaryY + 16);
        doc.setTextColor(0);
      });

      const drawSectionTitle = (title, y) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(title, marginX, y);
      };

      const sharedTableOptions = {
        theme: "grid",
        headStyles: { fillColor: [240, 240, 245], textColor: 20, fontStyle: "bold", fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: 30 },
        alternateRowStyles: { fillColor: [250, 250, 252] },
        styles: { cellPadding: 4, overflow: "linebreak", valign: "middle" },
        margin: { left: marginX, right: marginX },
      };

      let cursorY = summaryY + 36;
      let sectionNum = 0;

      if (currentType === 'all' || currentType === 'activity') {
        // --- Secretary / Assistant Secretary activities ---
        sectionNum++;
        const secRows = secretaryData.map(r => [
          r.activityId, r.datetime, r.meeting, r.committee, r.paper, r.document,
          activityTypeLabel(r.type), r.performedBy, r.changedField, r.previousValue, r.newValue,
        ]);
        drawSectionTitle(`Section ${sectionNum} — Secretary / Assistant Secretary activities (${secretaryData.length} records)`, cursorY);
        autoTable(doc, {
          ...sharedTableOptions,
          startY: cursorY + 8,
          head: [[
            "Activity log ID", "Activity date & time", "Meeting name", "Committee name",
            "Paper name", "Document name", "Activity type", "Performed by",
            "Changed field", "Previous value", "New value",
          ]],
          body: secRows,
          columnStyles: {
            8: { cellWidth: 70 },
            9: { cellWidth: 90 },
            10: { cellWidth: 90 },
          },
        });

        cursorY = doc.lastAutoTable.finalY + 28;

        // --- Member activities ---
        sectionNum++;
        const memRows = memberData.map(r => [
          r.activityId, r.datetime, r.meeting, r.committee, r.paper, r.document,
          activityTypeLabel(r.type), r.performedBy,
        ]);
        drawSectionTitle(`Section ${sectionNum} — Member activities (${memberData.length} records)`, cursorY);
        autoTable(doc, {
          ...sharedTableOptions,
          startY: cursorY + 8,
          head: [[
            "Activity log ID", "Activity date & time", "Meeting name", "Committee name",
            "Paper name", "Document name", "Activity type", "Performed by",
          ]],
          body: memRows,
        });

        cursorY = doc.lastAutoTable.finalY + 28;
      }

      if (currentType === 'all' || currentType === 'annotation') {
        // --- Annotation summary ---
        sectionNum++;
        const annRows = annotationData.map(r => [
          r.annotationId, r.datetime, r.meeting, r.committee, r.paper, r.document,
          r.page, activityTypeLabel(r.annotationType), activityTypeLabel(r.action),
          r.annotationType === 'VOICE_COMMENTED' ? '—' : (r.content || '—'),
          r.annotatedBy,
        ]);
        drawSectionTitle(`Section ${sectionNum} — Annotation summary (${annotationData.length} records)`, cursorY);
        autoTable(doc, {
          ...sharedTableOptions,
          startY: cursorY + 8,
          head: [[
            "Annotation log ID", "Date & time", "Meeting name", "Committee name",
            "Paper name", "Document name", "Page no.", "Annotation type",
            "Action", "Annotation content", "Annotated by",
          ]],
          body: annRows,
          columnStyles: {
            9: { cellWidth: 130 },
          },
        });
      }

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(110);
        doc.text(
          `Page ${i} of ${pageCount}  |  Generated by OCTOPLEX  |  Exported by: ${userName}  |  ${exportedAt}`,
          pageWidth / 2,
          pageHeight - 18,
          { align: "center" }
        );
        doc.setTextColor(0);
      }

      doc.save(`Activity_Log_Report_${dayjs().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
      notificationApi.error({
        message: 'PDF export failed',
        description: 'Unable to download the PDF report. Please try again later.',
        placement: 'bottomLeft',
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  const secretaryColumns = [
    { title: "Activity log ID", dataIndex: "activityId", key: "activityId" },
    { title: "Activity date & time", dataIndex: "datetime", key: "datetime" },
    { title: "Meeting name", dataIndex: "meeting", key: "meeting", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Committee name", dataIndex: "committee", key: "committee", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Paper name", dataIndex: "paper", key: "paper", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Document name", dataIndex: "document", key: "document", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    {
      title: "Activity type",
      dataIndex: "type",
      key: "type",
      render: (value) => <Tag color={activityTagColor(value)}>{activityTypeLabel(value)}</Tag>,
    },
    { title: "Performed by", dataIndex: "performedBy", key: "performedBy", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Changed field", dataIndex: "changedField", key: "changedField", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Previous value", dataIndex: "previousValue", key: "previousValue", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "New value", dataIndex: "newValue", key: "newValue", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
  ];

  const memberColumns = [
    { title: "Activity log ID", dataIndex: "activityId", key: "activityId" },
    { title: "Activity date & time", dataIndex: "datetime", key: "datetime" },
    { title: "Meeting name", dataIndex: "meeting", key: "meeting", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Committee name", dataIndex: "committee", key: "committee", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Paper name", dataIndex: "paper", key: "paper", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Document name", dataIndex: "document", key: "document", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    {
      title: "Activity type",
      dataIndex: "type",
      key: "type",
      render: (value) => <Tag color={activityTagColor(value)}>{activityTypeLabel(value)}</Tag>,
    },
    { title: "Performed by", dataIndex: "performedBy", key: "performedBy", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
  ];

  const annotationColumns = [
    { title: "Annotation log ID", dataIndex: "annotationId", key: "annotationId" },
    { title: "Date & time", dataIndex: "datetime", key: "datetime" },
    { title: "Meeting name", dataIndex: "meeting", key: "meeting", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Committee name", dataIndex: "committee", key: "committee", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Paper name", dataIndex: "paper", key: "paper", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Document name", dataIndex: "document", key: "document", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
    { title: "Page no.", dataIndex: "page", key: "page" },
    {
      title: "Annotation type",
      dataIndex: "annotationType",
      key: "annotationType",
      render: (value) => <Tag color={activityTagColor(value)}>{activityTypeLabel(value)}</Tag>,
    },
    {
      title: "Annotation content",
      dataIndex: "content",
      key: "content",
      align: "center",
      render: (value, record) => {
        const type = record.annotationType;
        if (type === 'VOICE_COMMENTED' || type === 'VOICE_DELETED' || type === 'COMMENT_DELETED') return '—';

        if (record.annotationType === 'TEXT_COMMENTED' || record.annotationType === 'COMMENT_UPDATED' || record.annotationType === 'REPLIED') {
          return (
            <Tooltip title={value}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() =>
                  setAnnotationViewer({
                    open: true,
                    content: value,
                    record,
                  })
                }
                size="small"
              />
            </Tooltip>
          );
        }

        return <span style={{ wordBreak: 'break-all' }}>{value}</span>;
      },
    },
    { title: "Annotated by", dataIndex: "annotatedBy", key: "annotatedBy", render: (v) => <span style={{ wordBreak: 'break-all' }}>{v || '—'}</span> },
  ];

  const SummaryStat = ({ value, label }) => (
    <Flex vertical align="flex-start" gap={2}>
      <Text style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.15 }}>
        {value}
      </Text>
      <Text type="secondary" style={{ fontSize: SIZES.small }}>
        {label}
      </Text>
    </Flex>
  );

  const tableContainerStyle = {
    padding: SIZES.large,
    background: colorBgContainer,
    borderRadius: borderRadiusLG,
    border: `1px solid ${colorBorderSecondary}`,
    marginBottom: SIZES.medium,
  };

  const totalEvents = secretaryData.length + memberData.length + annotationData.length;

  const downloadDisabled = totalEvents === 0;
  const downloadTooltip = !hasSearched
    ? 'Please run a search before downloading the report.'
    : 'No records found for the selected criteria.';

  return (
    <Spin spinning={loading}>
      {notificationContextHolder}
      
      <div style={{ padding: SIZES.large, maxWidth: "100%", overflowX: "hidden" }}>
        <Row style={{ marginBottom: 12 }}>
              <Col span={24}>
                <Breadcrumb
                  items={[
                    { href: '', title: <FileTextOutlined /> },
                    { title: 'Reports' },
                    { title: 'Activity Log Report' },
                  ]}
                />
              </Col>
            </Row>
        <Card
          bordered
          style={{
            marginBottom: SIZES.medium,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
          bodyStyle={{ padding: SIZES.large }}
        >
          <Title level={5} style={{ marginTop: 0, marginBottom: SIZES.medium }}>
            Filter criteria
          </Title>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              reportType: "all",
              dateFrom: dayjs(),
              dateTo: dayjs(),
              committee: 'all',
              meeting: '',
              paperName: '',
              documentName: '',
            }}
          >
            <Row gutter={[SIZES.medium, 0]}>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item
                  label="Report type"
                  name="reportType"
                  rules={[{ required: true, message: 'Report Type is required.' }]}
                >
                  <Select options={reportTypeOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item
                  label="Date from"
                  name="dateFrom"
                  rules={[{ required: true, message: 'Date From is required.' }]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item
                  label="Date to"
                  name="dateTo"
                  rules={[
                    { required: true, message: 'Date To is required.' },
                    {
                      validator: (_, value) => {
                        const dateFrom = form.getFieldValue('dateFrom');
                        if (value && dateFrom && value.isBefore(dateFrom, 'day')) {
                          return Promise.reject('Date To cannot be earlier than Date From.');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => {
                      const dateFrom = form.getFieldValue('dateFrom');
                      return dateFrom && current && current.isBefore(dateFrom, 'day');
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item label="Committee" name="committee">
                  <Select options={committees} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item label="Meeting name" name="meeting">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by meeting name..."
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item label="Paper name" name="paperName">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by paper name..."
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item label="Document name" name="documentName">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by document name..."
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xxl={4}>
                <Form.Item label="Performed by" name="performedBy">
                  <Input placeholder="Name or email..." />
                </Form.Item>
              </Col>
            </Row>

            <Flex
              justify="space-between"
              align="center"
              wrap="wrap"
              gap={SIZES.medium}
            >
              <Space>
                <Button type="primary" onClick={handleSearch}>Search</Button>
                <Button type="text" onClick={handleClear}>Clear</Button>
              </Space>
              <Text type="secondary">
                {totalEvents} records found
              </Text>
              <Space wrap>
                <Tooltip title={downloadDisabled ? downloadTooltip : ''}>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleDownloadExcel}
                    loading={loadingExcel}
                    disabled={downloadDisabled}
                  >
                    Download as Excel
                  </Button>
                </Tooltip>
                <Tooltip title={downloadDisabled ? downloadTooltip : ''}>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={handleDownloadPdf}
                    loading={loadingPdf}
                    disabled={downloadDisabled}
                  >
                    Download as PDF
                  </Button>
                </Tooltip>
              </Space>
            </Flex>
          </Form>
        </Card>

        <Card
          bordered
          style={{
            marginBottom: SIZES.medium,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
          bodyStyle={{ padding: SIZES.large }}
        >
          <Flex
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap={SIZES.xSmall}
          >
            <Title level={5} style={{ margin: 0 }}>
              Activity log report
            </Title>
          </Flex>
          <Row
            gutter={[SIZES.medium, SIZES.medium]}
            style={{ marginTop: SIZES.large }}
          >
            <Col xs={12} sm={12} md={6}>
              <SummaryStat value={totalEvents} label="Total events" />
            </Col>
            {(selectedType === 'all' || selectedType === 'activity') && (
              <Col xs={12} sm={12} md={6}>
                <SummaryStat value={secretaryData.length} label="Secretary activities" />
              </Col>
            )}
            {(selectedType === 'all' || selectedType === 'activity') && (
              <Col xs={12} sm={12} md={6}>
                <SummaryStat value={memberData.length} label="Member activities" />
              </Col>
            )}
            {(selectedType === 'all' || selectedType === 'annotation') && (
              <Col xs={12} sm={12} md={6}>
                <SummaryStat value={annotationData.length} label="Annotations" />
              </Col>
            )}
          </Row>
        </Card>

        {(selectedType === 'all' || selectedType === 'activity') && (
          <div style={tableContainerStyle}>
            <Title level={5} style={{ marginTop: 0, marginBottom: SIZES.medium }}>
              Secretary / Assistant Secretary activities
            </Title>
            <Table
              columns={secretaryColumns}
              dataSource={secretaryData}
              scroll={{ x: "max-content" }}
              pagination={standardPagination(secretaryData.length)}
            />
          </div>
        )}

        {(selectedType === 'all' || selectedType === 'activity') && (
          <div style={tableContainerStyle}>
            <Title level={5} style={{ marginTop: 0, marginBottom: SIZES.medium }}>
              Member activities
            </Title>
            <Table
              columns={memberColumns}
              dataSource={memberData}
              scroll={{ x: "max-content" }}
              pagination={standardPagination(memberData.length)}
            />
          </div>
        )}

        {(selectedType === 'all' || selectedType === 'annotation') && (
          <div style={tableContainerStyle}>
            <Title level={5} style={{ marginTop: 0, marginBottom: SIZES.medium }}>
              Annotation summary report
            </Title>
            <Table
              columns={annotationColumns}
              dataSource={annotationData}
              scroll={{ x: "max-content" }}
              pagination={standardPagination(annotationData.length)}
            />
          </div>
        )}

        <Modal
          open={annotationViewer.open}
          onCancel={() => setAnnotationViewer({ open: false, content: '', record: null })}
          footer={null}
          width={400}
        >
          {annotationViewer.record && (
            <div>
              <div style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>
                {annotationViewer.content}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Spin>
  );
}
